// Birthday Utility Functions

/**
 * Check if today is user's birthday
 * @param {string} dateOfBirth - User's date of birth (YYYY-MM-DD format)
 * @returns {boolean} - True if today is user's birthday
 */
export const isTodayUserBirthday = (dateOfBirth) => {
  if (!dateOfBirth) {
    console.log('❌ No dateOfBirth provided');
    return false;
  }
  
  try {
    console.log('🔍 Checking birthday for DOB:', dateOfBirth);
    
    const today = new Date();
    let birthDate;
    
    // Handle different date formats
    if (typeof dateOfBirth === 'string') {
      // Handle ISO string or date string
      birthDate = new Date(dateOfBirth);
    } else if (typeof dateOfBirth === 'object' && dateOfBirth.$date) {
      // Handle MongoDB date object
      birthDate = new Date(dateOfBirth.$date);
    } else {
      // Try to convert to date
      birthDate = new Date(dateOfBirth);
    }
    
    // Validate the date
    if (isNaN(birthDate.getTime())) {
      console.error('❌ Invalid date format:', dateOfBirth);
      return false;
    }
    
    // Compare month and day only (ignore year)
    const todayMonth = today.getMonth();
    const todayDay = today.getDate();
    const birthMonth = birthDate.getMonth();
    const birthDay = birthDate.getDate();
    
    console.log('📅 Today:', `${todayMonth + 1}/${todayDay}`);
    console.log('🎂 Birth:', `${birthMonth + 1}/${birthDay}`);
    
    const isBirthday = todayMonth === birthMonth && todayDay === birthDay;
    console.log('🎯 Birthday match:', isBirthday);
    
    return isBirthday;
  } catch (error) {
    console.error('❌ Error checking birthday:', error);
    return false;
  }
};

/**
 * Calculate user's age
 * @param {string} dateOfBirth - User's date of birth (YYYY-MM-DD format)
 * @returns {number} - User's current age
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return 0;
  
  try {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  } catch (error) {
    console.error('Error calculating age:', error);
    return 0;
  }
};

/**
 * Get birthday celebration messages
 * @param {string} userName - User's name
 * @param {number} age - User's age
 * @returns {Array} - Array of birthday messages
 */
export const getBirthdayMessages = (userName, age) => {
  return [
    `🎉 Happy Birthday ${userName}! 🎉`,
    `🎂 Shahji Computers की तरफ से 🎂`,
    `🎈 ${age} साल की हार्दिक शुभकामनाएं! 🎈`,
    `🌟 May all your dreams come true! 🌟`,
    `🎊 Have a wonderful year ahead! 🎊`,
    `💖 Thank you for being part of our team! 💖`
  ];
};

/**
 * Create birthday music audio element
 * @returns {HTMLAudioElement} - Audio element for birthday music
 */
export const createBirthdayMusic = () => {
  // Create a simple birthday tune using Web Audio API
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  
  // Birthday song notes (Happy Birthday melody)
  const notes = [
    { freq: 261.63, duration: 0.5 }, // C
    { freq: 261.63, duration: 0.5 }, // C
    { freq: 293.66, duration: 1 },   // D
    { freq: 261.63, duration: 1 },   // C
    { freq: 349.23, duration: 1 },   // F
    { freq: 329.63, duration: 2 },   // E
    
    { freq: 261.63, duration: 0.5 }, // C
    { freq: 261.63, duration: 0.5 }, // C
    { freq: 293.66, duration: 1 },   // D
    { freq: 261.63, duration: 1 },   // C
    { freq: 392.00, duration: 1 },   // G
    { freq: 349.23, duration: 2 },   // F
  ];
  
  let currentTime = audioContext.currentTime;
  
  const playBirthdayTune = () => {
    notes.forEach((note, index) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(note.freq, currentTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, currentTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.01, currentTime + note.duration);
      
      oscillator.start(currentTime);
      oscillator.stop(currentTime + note.duration);
      
      currentTime += note.duration;
    });
  };
  
  return {
    play: playBirthdayTune,
    stop: () => {
      // Audio context will automatically clean up oscillators
    }
  };
};

/**
 * Show birthday notification
 * @param {string} userName - User's name
 */
export const showBirthdayNotification = (userName) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(`🎉 Happy Birthday ${userName}!`, {
      body: 'Shahji Computers wishes you a very happy birthday!',
      icon: '/birthday-icon.png',
      tag: 'birthday-notification'
    });
  }
};

/**
 * Store birthday celebration status in localStorage
 * @param {string} userId - User's ID
 * @param {string} date - Current date
 */
export const markBirthdayCelebrated = (userId, date) => {
  const key = `birthday_celebrated_${userId}`;
  localStorage.setItem(key, date);
};

/**
 * Check if birthday was already celebrated today
 * @param {string} userId - User's ID
 * @returns {boolean} - True if already celebrated today
 */
export const wasBirthdayCelebratedToday = (userId) => {
  const key = `birthday_celebrated_${userId}`;
  const celebratedDate = localStorage.getItem(key);
  const today = new Date().toDateString();
  
  return celebratedDate === today;
};
